import { Button, Callout, Card, Field, Input, Skeleton, useToast } from '@provenance/design';
import { setPasswordSchema, type SetPasswordInput } from '@provenance/shared';
import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQuery } from '@tanstack/react-query';
import { AlertTriangle, CheckCircle2 } from 'lucide-react';
import { useForm } from 'react-hook-form';
import { Link, useSearchParams } from 'react-router-dom';
import { ApiError, api } from '../lib/api';

/**
 * Setting a password from an emailed link.
 *
 * This page is the whole reason inviting an operator now works. The link had always been
 * sent — `${CONSOLE_URL}/activate?token=…` — and the console had no route for it, because
 * signed out it rendered the sign-in form for every path. So an invited operator, who has no
 * password, landed on a password prompt, and the sign-in page told them to ask for a reset
 * link that nothing could send. A closed loop with no exit.
 *
 * TWO THINGS IT DOES DELIBERATELY:
 *
 *  1. **It checks the link before drawing the form.** Finding out that an invitation expired
 *     after choosing a password and typing it twice is a small cruelty, and the answer never
 *     depended on the password. Asking does not spend the token.
 *
 *  2. **It states the password rule up front**, rather than as an error after a rejected
 *     attempt. The rule is length, not punctuation — see `passwordSchema`.
 *
 * The same Zod schema validates here and at the API, so the two cannot drift apart. The
 * route is also mounted at `/reset`: the API redeems a reset token through the same endpoint,
 * so when T-116 adds the reset trigger the page is already here.
 */
export function SetPassword() {
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const toast = useToast();

  const link = useQuery({
    queryKey: ['activation', token],
    queryFn: () =>
      api.get<{ firstName: string; email: string; chamberName: string | null; hasPassword: boolean }>(
        `/api/console/users/activation/${encodeURIComponent(token)}`,
      ),
    enabled: token.length > 0,
    retry: false,
  });

  const form = useForm<SetPasswordInput>({
    resolver: zodResolver(setPasswordSchema),
    defaultValues: { token, password: '', confirmPassword: '' },
  });

  const submit = useMutation({
    mutationFn: (values: SetPasswordInput) =>
      api.post('/api/console/users/activate', {
        token: values.token,
        password: values.password,
        confirmPassword: values.confirmPassword,
      }),
    onSuccess: () =>
      toast.success('Password set', 'Sign in with your email address and your new password.'),
    onError: (error) =>
      toast.error(
        'Could not set your password',
        error instanceof ApiError ? error.message : 'Something went wrong.',
      ),
  });

  return (
    <main className="grid min-h-screen place-items-center px-4 py-10">
      <div className="w-full max-w-[26rem]">
        <span className="pv-seal-ring mx-auto mb-5 block size-10" aria-hidden />

        {!token ? (
          <Dead
            title="That link is incomplete"
            body="It may have been cut in half by your email program. Copy the whole address from the email, or ask your chamber administrator to send it again."
          />
        ) : link.isLoading ? (
          <Card>
            <Skeleton className="h-6 w-2/3" />
            <Skeleton className="mt-3 h-4 w-full" />
            <Skeleton className="mt-5 h-9 w-full" />
          </Card>
        ) : link.isError ? (
          <Dead
            title="That link cannot be used"
            body={
              link.error instanceof ApiError
                ? link.error.message
                : 'Ask your chamber administrator to send the invitation again.'
            }
          />
        ) : submit.isSuccess ? (
          <Card>
            <div className="flex flex-col items-center gap-3 text-center">
              <CheckCircle2 aria-hidden className="size-7 text-[var(--pv-leaf-ink)]" />
              <h1 className="[font-size:var(--pv-text-h2)] font-semibold">Your password is set</h1>
              <p className="[font-size:13.5px] text-[var(--pv-muted)]">
                Sign in as <span className="pv-mono">{link.data?.email}</span>.
              </p>
              <Button asChild variant="primary" className="mt-1 w-full">
                <Link to="/">Go to sign in</Link>
              </Button>
            </div>
          </Card>
        ) : (
          <Card>
            <h1 className="[font-size:var(--pv-text-h2)] font-semibold">
              {link.data?.hasPassword ? 'Choose a new password' : `Welcome, ${link.data?.firstName}`}
            </h1>
            <p className="mt-1.5 [font-size:13.5px] text-[var(--pv-muted)]">
              {link.data?.hasPassword
                ? 'This replaces your current password.'
                : `Set a password for ${link.data?.chamberName ?? 'your chamber'}, and you are in.`}{' '}
              Your sign-in address is <span className="pv-mono">{link.data?.email}</span>.
            </p>

            <form
              className="mt-5 flex flex-col gap-3.5"
              onSubmit={form.handleSubmit((values) => submit.mutate(values))}
              noValidate
            >
              <Field
                label="New password"
                required
                // Said before it can be broken, not after. Length beats punctuation, and a
                // rule an operator meets first time is one they do not write on a note.
                help="At least 12 characters. A short phrase is easier to remember and harder to guess."
                error={form.formState.errors.password?.message}
              >
                <Input
                  type="password"
                  autoComplete="new-password"
                  autoFocus
                  {...form.register('password')}
                />
              </Field>

              <Field
                label="Type it again"
                required
                error={form.formState.errors.confirmPassword?.message}
              >
                <Input type="password" autoComplete="new-password" {...form.register('confirmPassword')} />
              </Field>

              <Button type="submit" variant="primary" loading={submit.isPending} className="mt-1 w-full">
                Set my password
              </Button>
            </form>
          </Card>
        )}
      </div>
    </main>
  );
}

/** A link that cannot be used. Says which way it failed, and what to do about it. */
function Dead({ title, body }: { title: string; body: string }) {
  return (
    <Card>
      <Callout tone="stop" icon={<AlertTriangle className="size-4" />} title={title}>
        {body}
      </Callout>
      <Button asChild variant="secondary" className="mt-4 w-full">
        <Link to="/">Back to sign in</Link>
      </Button>
    </Card>
  );
}
